/**
 * TowerMarquee — "From Tower" system announcements
 *
 * Dismissible banner at the top of SocialShell showing platform news,
 * classroom funding, or global events. Filters to system/announcement posts
 * from the last 24 hours.
 *
 * Dismissed state persists per session (sessionStorage).
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import { useSystemAnnouncements } from './queries';

interface TowerMarqueeProps {
  clubId: string;
  theme: ClubTheme;
}

export default function TowerMarquee({ clubId, theme }: TowerMarqueeProps) {
  const { data: announcements = [] } = useSystemAnnouncements(clubId);
  const [dismissed, setDismissed] = useState(false);

  const storageKey = `tower-marquee-dismissed-${clubId}`;

  useEffect(() => {
    const stored = sessionStorage.getItem(storageKey);
    if (stored) setDismissed(true);
  }, [storageKey]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(storageKey, '1');
  };

  // Nothing to show
  if (dismissed || announcements.length === 0) return null;

  // Show the most recent announcement
  const latest = announcements[0];
  const body = (latest.body || '').slice(0, 120);

  return (
    <div
      className="relative mx-auto mb-4 flex max-w-6xl items-center gap-3 rounded-xl px-4 py-2.5"
      style={{
        backgroundColor: `${theme.colors.primary}12`,
        border: `1px solid ${theme.colors.primary}25`,
      }}
    >
      {/* Tower icon */}
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
        style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
      >
        T
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: theme.colors.primary }}>
          From Tower
        </span>
        <p className="text-xs truncate" style={{ color: theme.colors.text }}>
          {body}{body.length >= 120 ? '...' : ''}
        </p>
      </div>

      {/* Count badge if multiple */}
      {announcements.length > 1 && (
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold"
          style={{ backgroundColor: `${theme.colors.primary}20`, color: theme.colors.primary }}
        >
          +{announcements.length - 1} more
        </span>
      )}

      {/* Dismiss */}
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded-lg p-1 transition-colors hover:opacity-70"
        style={{ color: theme.colors.muted }}
        aria-label="Dismiss"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
